import { Trans } from "@lingui/macro";
import { forwardRef, useEffect } from "react";
import { CloseButton } from "src/atoms/CloseButton";
import { Spinner } from "src/icons/Spinner";
import useBrandSizeGuideBySlug from "src/hooks/useBrandSizeGuideBySlug";
import { StyledSizeGuide } from "./SizeGuide.styled";
import { Box, Flex } from "@otrium/core";
import { sanitize } from "isomorphic-dompurify";

interface Props {
  brandSlug: string;
  onClose: () => void;
  productGenderId: string | undefined;
}

const SizeGuide = forwardRef<HTMLDivElement, Props>(
  ({ brandSlug, onClose, productGenderId }, ref) => {
    const genderId = productGenderId;

    const { loading, data } = useBrandSizeGuideBySlug(brandSlug);

    if (loading) {
      return (
        <Flex
          width="100vw"
          height="100vh"
          maxWidth="600px"
          marginLeft={["space4", "space4", "space0"]}
          justifyContent="center"
          alignItems="center"
        >
          <Box
            sx={{
              svg: {
                width: "24px",
                height: "24px",
                stroke: "tone.black",
              },
            }}
          >
            <Spinner />
          </Box>
        </Flex>
      );
    }

    const sizeGuide =
      data?.brandSizeGuideBySlug?.find(({ gender }) => gender === genderId)
        ?.text || "";

    return (
      <StyledSizeGuide ref={ref}>
        <div className="SizeGuide__header">
          <div data-testid="size-guide-title">
            <Trans>Size Guide</Trans>
          </div>
          <CloseButton className="SizeGuide__close" onClick={onClose} />
        </div>
        <BrandGenderSizeGuide sizeGuide={sizeGuide} />
      </StyledSizeGuide>
    );
  }
);

SizeGuide.displayName = "SizeGuide";

// [TODO: move to its own compoent under molecules]
function BrandGenderSizeGuide({ sizeGuide }: { sizeGuide: string }) {
  useEffect(() => {
    // Creates wrapper around each table node to create a
    // horizontal scroll for each table
    // <div className="SizeGuide__tableContainer">
    //   <table>...</table>
    // </div>
    const tables = document.querySelectorAll<HTMLTableElement>(
      ".SizeGuide__content table"
    );

    tables.forEach((table) => {
      const container = document.createElement("div");
      container.className = "SizeGuide__tableContainer";

      if (table.parentNode) {
        table.parentNode.insertBefore(container, table);
      }
      container.appendChild(table);
    });

    // Hides icon if image haven't loaded
    const images = document.querySelectorAll<HTMLImageElement>(
      ".SizeGuide__content img"
    );

    images.forEach((image) => {
      image.onerror = function () {
        this.style.display = "none";
      };
    });
  }, []);
  return (
    <div
      className="SizeGuide__content"
      dangerouslySetInnerHTML={{ __html: sanitize(sizeGuide) }}
    />
  );
}

export default SizeGuide;
